import React, { useState, useEffect } from "react";
import {
  Sidebar,
  Menu,
  MenuItem,
  SubMenu,
  useProSidebar,
} from "react-pro-sidebar";
import {
  ArticleOutlined,
  Badge,
  ContactMail,
  Flag,
  FormatQuote,
  History,
  Home,
  HowToReg,
  List,
  LocalShipping,
  Password,
  PhotoCamera,
  PrivacyTip,
  Report,
  SelectAllOutlined,
  SellOutlined,
  TocOutlined,
  ViewHeadline,
} from "@mui/icons-material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CodeIcon from "@mui/icons-material/Code";
import DataObjectIcon from "@mui/icons-material/DataObject";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MessageIcon from "@mui/icons-material/Message";
import PaymentsIcon from "@mui/icons-material/Payments";
import EmailIcon from "@mui/icons-material/Email";
import Diversity1Icon from "@mui/icons-material/Diversity1";
import ForumIcon from "@mui/icons-material/Forum";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import GavelIcon from "@mui/icons-material/Gavel";
import ArticleIcon from "@mui/icons-material/Article";
import CollectionsIcon from "@mui/icons-material/Collections";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import NotificationPip from "./NotificationPip";
import { useDispatch } from "react-redux";
import { get_admin_dashboard_counts } from "../reduxData/user/userAction";

const AdminSideBar = () => {
  const [open, setOpen] = useState(null);
  const location = useLocation();
  const [counts, setCounts] = useState({});
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (user) {
      const fetchCounts = async () => {
        try {
          const data = await get_admin_dashboard_counts(dispatch);
          if (data) {
            // Combine Affiliate Applications + Payout Requests into one notification count
            // This satisfies the "part of the same count" requirement
            data.affiliateTotal =
              (data.pendingAffiliates || 0) + (data.payoutRequests || 0);
            setCounts(data);
          }
        } catch (error) {
          console.error("Failed to fetch notification counts:", error);
        }
      };

      fetchCounts();
      const interval = setInterval(fetchCounts, 1800000);
      return () => clearInterval(interval);
    }
  }, [user, dispatch]);

  const items = [
    {
      name: "Categories",
      value: "/category",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="1.8em"
          height="1.8em"
          viewBox="0 0 24 24"
        >
          <g
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          >
            <circle cx="17" cy="7" r="3" />
            <circle cx="7" cy="17" r="3" />
            <path d="M14 14h6v5a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1zM4 4h6v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
          </g>
        </svg>
      ),
      subitem: [],
      key: "p_categories",
    },
    {
      name: "Users",
      value: "/",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="1.8em"
          height="1.8em"
          viewBox="0 0 24 24"
        >
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M17 19.5c0-1.657-2.239-3-5-3s-5 1.343-5 3m14-3c0-1.23-1.234-2.287-3-2.75M3 16.5c0-1.23 1.234-2.287 3-2.75m12-4.014a3 3 0 1 0-4-4.472M6 9.736a3 3 0 0 1 4-4.472m2 8.236a3 3 0 1 1 0-6a3 3 0 0 1 0 6"
          />
        </svg>
      ),
      subitem: [],
      key: "p_users",
    },
    {
      name: "Stores",
      value: "/store-management",
      icon: <AddBusinessIcon />,
      subitem: [],
      key: "p_store_manage",
      countKey: "pendingStores",
    },
    {
      name: "Affiliates",
      value: "/affiliate-management",
      icon: <Diversity1Icon />,
      subitem: [],
      key: "p_affiliate_manage",
      countKey: "affiliateTotal",
    },
    {
      name: "Auctions",
      value: "/item-listing",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="1.8em"
          height="1.8em"
          viewBox="0 0 1024 1024"
        >
          <path
            fill="currentColor"
            d="M704 192h160v736H160V192h160v64h384zM288 512h448v-64H288zm0 256h448v-64H288zm96-576V96h256v96z"
          />
        </svg>
      ),
      subitem: [],
      key: "p_auctions",
      countKey: "pendingAuctions",
    },
    {
      name: "Collections",
      value: "/collections",
      icon: <CollectionsIcon />,
      subitem: [],
      key: "p_collections",
    },
    {
      name: "Community",
      value: "",
      icon: <ForumIcon />,
      key: "p_community",
      countKey: "pendingReports",
      subitem: [
        {
          name: "Rooms",
          value: "/community/rooms",
          icon: <MeetingRoomIcon />,
        },
        {
          name: "Posts & Reports",
          value: "/community/posts-reports",
          icon: <GavelIcon />,
        },
        {
          name: "Admin Posts",
          value: "/cms-pages/posts",
          icon: <ArticleIcon />,
        },
      ],
    },
    {
      name: "Play Later",
      value: "/play-later",
      icon: <PaymentsIcon />,
      subitem: [],
      key: "p_play_later",
      countKey: "playlatertotal",
    },
    {
      name: "Developer",
      value: "/dev?dev=true",
      icon: <CodeIcon />,
      subitem: [],
      key: "p_dev_tools",
    },
    {
      name: "Data",
      value: "/data",
      icon: <DataObjectIcon />,
      subitem: [
        // {
        //   name: "Auction Data",
        //   value: "/auctionsdata",
        //   icon: (
        //     <svg
        //       xmlns="http://www.w3.org/2000/svg"
        //       width="1.8em"
        //       height="1.8em"
        //       viewBox="0 0 1024 1024"
        //     >
        //       <path
        //         fill="currentColor"
        //         d="M704 192h160v736H160V192h160v64h384zM288 512h448v-64H288zm0 256h448v-64H288zm96-576V96h256v96z"
        //       />
        //     </svg>
        //   ),
        // },
        {
          name: "Autobids",
          value: "/autobiddata",
          icon: <MonetizationOnIcon />,
        },
        {
          name: "Bids",
          value: "/bid-management",
          icon: <AttachMoneyIcon />,
        },
      ],
      key: "p_data_tools",
    },

    {
      name: "Shipments",
      value: "/ib-shipping",
      icon: <LocalShippingIcon />,
      subitem: [],
      key: "ib_shipping",
      countKey: "newShippingRequests",
    },
    {
      name: "Contact Users",
      value: "",
      icon: <ConnectWithoutContactIcon />,
      subitem: [
        {
          name: "Notify Users",
          value: "/notify-users",
          icon: <NotificationsActiveIcon />,
          subitem: [],
          key: "p_notify_user",
        },
        {
          name: "Email Users",
          value: "/email-users",
          icon: <EmailIcon />,
          subitem: [],
          key: "p_email_user",
        },
        {
          name: "Conversations",
          value: "/conversations",
          icon: <MessageIcon />,
          subitem: [],
          key: "p_conversations",
        },
      ],
    },

    {
      name: "Reports",
      value: "",
      icon: <List />,
      subitem: [
        {
          name: "Sales History",
          value: "/sales-history",
          icon: <History />,
        },
        {
          name: "User Reports",
          value: "/user-reports",
          icon: <Flag />,
        },
      ],
      key: "p_reports",
      countKey: "newUserReports",
    },
    {
      name: "Badge System",
      value: "/badge-system",
      icon: <Badge />,
      subitem: [
        {
          name: "Levels",
          value: "/badge",
          icon: <EmojiEventsIcon />,
        },
        {
          name: "Awards",
          value: "/awards",
          icon: <MilitaryTechIcon />,
        },
      ],
      key: "p_badge_system",
    },
    {
      name: "Disputes",
      value: "/disputes",
      icon: <Badge />,
      subitem: [],
      key: "p_disputes",
      countKey: "newDisputes",
    },
    {
      name: "Events",
      value: "/events",
      icon: <LocalActivityIcon />,
      subitem: [],
      key: "p_events",
    },
    {
      name: "Flag Comments",
      value: "/flag-comments",
      icon: <Flag />,
      subitem: [],
      key: "p_flags",
      countKey: "newFlagReports",
    },
    {
      name: "Staff Management",
      value: "/staff",
      icon: <Badge />,
      subitem: [],
      key: "p_staff",
    },
    {
      name: "CMS Pages",
      value: "/cms-pages",
      icon: <TocOutlined />,
      subitem: [
        {
          name: "Header",
          value: "/cms-pages/header",
          icon: <ViewHeadline />,
        },
        {
          name: "Home Pages",
          value: "/cms-pages/homepages",
          icon: <Home />,
        },
        {
          name: "Blogs",
          value: "/cms-pages/blogs",
          icon: <ArticleOutlined />,
        },
        {
          name: "Home Spotlight",
          value: "/cms-pages/blog-spotlight",
          icon: <ArticleOutlined />,
        },
        {
          name: "Sell an Item",
          value: "/cms-pages/sell-an-item",
          icon: <SelectAllOutlined />,
        },
        {
          name: "Hall of fame",
          value: "/cms-pages/hall-of-fame",
          icon: <EmojiEventsIcon />,
        },
        {
          name: "FAQ's",
          value: "/cms-pages/faq",
          icon: <FormatQuote />,
        },
        {
          name: "Privacy Policy",
          value: "/cms-pages/privacy-policy",
          icon: <PrivacyTip />,
        },
        {
          name: "Terms and Conditions",
          value: "/cms-pages/terms",
          icon: <FormatQuote />,
        },
        {
          name: "What's InsertBid",
          value: "/cms-pages/what-is-insert-bids",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1.8em"
              height="1.8em"
              viewBox="0 0 24 24"
            >
              <g fill="none" fillRule="evenodd">
                <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035c-.01-.004-.019-.001-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427c-.002-.01-.009-.017-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093c.012.004.023 0 .029-.008l.004-.014l-.034-.614c-.003-.012-.01-.02-.02-.022m-.715.002a.023.023 0 0 0-.027.006l-.006.014l-.034.614c0 .012.007.02.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z" />
                <path
                  fill="currentColor"
                  d="M2.686 10.462a2.5 2.5 0 0 0 0 3.536l2.829 2.828a2.5 2.5 0 0 0 4.242-2.121l.973-.973l6.078 7.014a2.793 2.793 0 1 0 3.94-3.94l-7.013-6.079l.972-.972a2.5 2.5 0 0 0 2.121-4.243L14 2.685a2.5 2.5 0 0 0-4.243 2.122l-4.95 4.949a2.496 2.496 0 0 0-2.12.707M12.94 8.695l-4.242 4.242l-2.122-2.121l4.243-4.243zM4.1 12.584a.5.5 0 1 1 .707-.708l2.829 2.829a.5.5 0 1 1-.707.707zm7.778-7.779a.5.5 0 1 1 .707-.707l2.829 2.829a.5.5 0 1 1-.707.707zm6.442 14.631l-6.008-6.932l.194-.195l6.933 6.008a.793.793 0 1 1-1.12 1.12"
                />
              </g>
            </svg>
          ),
        },
        {
          name: "Photo Guide",
          value: "/cms-pages/photo-guide",
          icon: <PhotoCamera />,
        },
        {
          name: "Collectibles Shipping",
          value: "/cms-pages/collectibles-shipping",
          icon: <LocalShipping />,
        },
        {
          name: "About Us",
          value: "/cms-pages/about-us",
          icon: <FormatQuote />,
        },
        {
          name: "Reputation System",
          value: "/cms-pages/rep-system",
          icon: <StarIcon />,
        },
        {
          name: "Contact Us",
          value: "/cms-pages/contact-us",
          icon: <ContactMail />,
        },
        {
          name: "How It Works",
          value: "/cms-pages/how-it-works",
          icon: <HowToReg />,
        },
      ],
      key: "p_cms",
    },
  ];

  const visibleItems = items.filter(
    (item) =>
      item.key !== "p_dev_tools" ||
      process.env.REACT_APP_SHOW_DEV_TOOLS === "true",
  );

  const { broken } = useProSidebar();

  return (
    <Sidebar
      breakPoint="md"
      backgroundColor="#fff"
      rootStyles={{
        borderRight: "1px solid #e0e0e0",
        position: broken ? "fixed" : "sticky",
        top: "75px !important",
        height: "calc(100vh - 75px) !important",
      }}
    >
      <Menu>
        {visibleItems.map((item) => {
          const count = item.countKey ? counts[item.countKey] : 0;

          return (
            <React.Fragment key={item.name}>
              {(user?.role === "staff" && user?.permission[item?.key] > 0) ||
              user?.role !== "staff" ? (
                item.subitem.length === 0 ? (
                  <MenuItem
                    className={
                      item?.value === location.pathname
                        ? "is-active"
                        : "not-active"
                    }
                    component={<Link to={`${item?.value}`} />}
                    onClick={() => setOpen(null)}
                    suffix={<NotificationPip count={count} />}
                  >
                    {item.icon} {item.name}
                  </MenuItem>
                ) : (
                  <SubMenu
                    label={item.name}
                    icon={item.icon}
                    suffix={<NotificationPip count={count} />}
                  >
                    {item.subitem.map((subItem) => (
                      <MenuItem
                        key={subItem.name}
                        className={
                          subItem?.value === location.pathname
                            ? "is-active"
                            : "not-active"
                        }
                        component={<Link to={`${subItem?.value}`} />}
                      >
                        {subItem.icon} {subItem.name}
                      </MenuItem>
                    ))}
                  </SubMenu>
                )
              ) : (
                ""
              )}
            </React.Fragment>
          );
        })}
        {user?.role === "staff" && (
          <MenuItem
            className={
              location.pathname === "/change-password"
                ? "is-active"
                : "not-active"
            }
            component={<Link to={"/change-password"} />}
          >
            <Password /> Change Password
          </MenuItem>
        )}
      </Menu>
    </Sidebar>
  );
};

export default AdminSideBar;
